<template>
	<InsightsDrawer
		v-if="showDayInsights"
		:title="$t('Insights') + ' - ' + headerDate"
		sessionStateKey="insights.day"
		:defaultDateRange="dayAsInterval"
		:showPupilSelector="true"
		:showDateRangeSelector="false"
		:insights="[
			{ value: 'activity', label: this.$tc('Activity', 2) },
			{ value: 'feedback', label: this.$t('Pupil feedback') },
		]"
		@requestClose="
			() => {
				this.showDayInsights = null;
			}
		"
	/>

	<AgendaGoalEvaluation
		v-if="showGoalEvaluation"
		:title="$tc('Goal', 2) + ' - ' + headerDate"
		:dateRange="dayAsInterval"
		@requestClose="showGoalEvaluation = null"
	/>

	<div class="border-b-4 py-1 sm:px-3 sm:gap-2 flex items-center justify-between" style="border-color: #fbbd23">
		<div
			class="flex flex-col"
			:class="[isPast ? 'text-base-content-light' : '']"
		>
			<span class="leading-5 text-lg">{{ headerDate }}</span>
			<span class="leading-none text-sm">{{ headerDay }}</span>
		</div>
		<div class="flex-grow-0 relative">
			<!-- <button class="btn btn-ghost p-1 min-h-0 h-auto" @click="toggleCopyDayDialog">
					<DocumentDuplicateIcon class="w-4 h-4" />
			</button> -->
			<button type="button" class="hidden md:inline-block btn btn-ghost bg-base-100 p-1 min-h-0 h-auto" @click="$refs.menu.open()">
				<EllipsisVerticalIcon class="w-5 h-5" />
			</button>
			<!-- Teleported into <body>: FullCalendar renders this header in a cell that clips its overflow -->
			<Dropdown ref="menu" position="bottom-end" teleport>
				<!-- close once an item is chosen: the items are not focusable, so the menu would otherwise stay open -->
				<DropdownMenu class="w-48" @click="$refs.menu.close()">
					<DropdownMenuItem v-if="canShowGoalEvaluations" :disabled="!hasLessons">
						<a @click="openGoalEvaluations()">
							<MapPinIcon class="w-4 h-4"/>
							{{ $t('Evaluate goals') }}
						</a>
					</DropdownMenuItem>
					<DropdownMenuItem :disabled="!isTodayOrPast || !hasLessons">
						<a @click="openInsights()">
							<ChartBarIcon class="w-4 h-4"/>
							{{ $t('Insights') }}
						</a>
					</DropdownMenuItem>
					<DropdownMenuItem :disabled="!hasLessons">
						<a @click="cloneDay">
							<DocumentDuplicateIcon class="w-4 h-4" />
							{{ $t("Copy to") }}...
						</a>
					</DropdownMenuItem>
					<DropdownMenuItem :disabled="!hasLessons">
						<a @click="clearDay">
							<TrashIcon class="w-4 h-4" />
							{{ $t("Clear day schedule") }}
						</a>
					</DropdownMenuItem>
				</DropdownMenu>
			</Dropdown>
		</div>
	</div>

	<Modal v-if="showCopyDayDialog" @requestUnmount="toggleCopyDayDialog">
		<template #header>
			<div class="font-semibold text-xl mb-4">
				{{ $t("Copy schedule of") }} &nbsp;<span class="inline-block border-b-2 border-primary">{{
					dialogDate
				}}</span>
			</div>
		</template>

		<template #default="modalActions">
			<template v-if="lessonsStore.errors">
				<div class="alert alert-error mb-4 w-full sm:max-w-2xl m-auto">
					{{ lessonsStore.errors?.toLocaleString($i18n) }}
				</div>
			</template>

			<div class="pl-2">{{ $t("to") }}...</div>

			<select
				class="select select-bordered w-full mb-4"
				v-model="selectedWeekToCopyTo"
				@change="updateWeekdaysToCopyTo"
			>
				<option v-for="week in weeksToCopyTo" :key="week.key" :value="week.value" :selected="true">
					{{ week.label }}
				</option>
			</select>

			<div class="pl-2">
				<span
					v-for="day in weekdaysToCopyTo"
					class="block mb-2"
					:class="{ 'opacity-30': !day.selectable }"
					:title="day.cantCopyReason"
				>
					<input
						type="checkbox"
						name="weekday"
						:value="day.value"
						:id="'wd-' + day.value"
						:disabled="!day.selectable"
						v-model="selectedDays"
					/>
					<label class="ml-2" :for="'wd-' + day.value">
						{{ day.name }}
						<span>
							{{
								day.date.toLocaleString({
									day: "numeric",
									month: "short",
								})
							}}
						</span>
						<span v-if="day.isCopySource" class="text-xs">({{ $t("selected") }})</span>
					</label>
				</span>
			</div>

			<template v-if="showOutdatedConfirmation">
				<div class="alert alert-error mb-4 w-full sm:max-w-2xl m-auto flex flex-col">
					{{
						$t('#error: agenda-outdated-lesson-plans')
					}}
					<div class="flex flex-col gap-2 text-neutral">
						<label class="flex items-center gap-2">
							<input type="radio" class="radio radio-sm bg-white border-neutral" v-model="updateOutdatedLessonPlans" :value="true" />
							{{ $t('Update lesson plans') }}
						</label>
						<label class="flex items-center gap-2">
							<input type="radio" class="radio radio-sm bg-white border-neutral" v-model="updateOutdatedLessonPlans" :value="false" />
							{{ $t('Keep old lesson plans') }}
						</label>
					</div>
				</div>
			</template>

			<template v-if="requiresOverrideConfirmation">
				<div class="alert alert-warning mb-4 w-full sm:max-w-2xl m-auto">
					{{
						$t(
							"Warning: (some) selected dates already contain a schedule. These schedules will be overwritten if you choose to continue."
						)
					}}
				</div>
			</template>

			<div class="modal-footer flex justify-end gap-1">
				<button class="btn btn-ghost" @click="modalActions.hide" :disabled="copyingDay">
					{{ $t("Cancel") }}
				</button>

				<template v-if="requiresOverrideConfirmation">
					<button class="btn btn-primary" @click="copyToSelectedDays" :disabled="copyingDay">
						{{ $t("Overwrite") }}
					</button>
				</template>

				<template v-else>
					<button class="btn btn-primary" @click="copyToSelectedDays" :disabled="copyingDay">
						{{ $t("Copy") }}
					</button>
				</template>
			</div>
		</template>
	</Modal>

	<Modal v-if="showClearDayConfirmation" @requestUnmount="closeClearDayConfirmation">
		<template #header>&nbsp;</template>

		<template #default="modalActions">
			{{ $t("Are you sure you want to delete the entire day's schedule?") }}

			<template v-if="lessonsStore.errors">
				<div class="alert alert-error mb-4 w-full sm:max-w-2xl m-auto">
					{{ lessonsStore.errors.toLocaleString($i18n) }}
				</div>
			</template>

			<div class="modal-footer flex justify-end gap-1">
				<button class="btn btn-ghost" @click="modalActions.hide">{{ $t("Cancel") }}</button>
				<button class="btn btn-danger" @click="confirmClearDay()" :class="[clearingDay ? 'loading' : '']">
					{{ $t("Clear day schedule") }}
				</button>
			</div>
		</template>
	</Modal>
</template>

<script lang="ts">
import { DateTime, Interval } from "luxon";
import {
	DocumentDuplicateIcon,
	EllipsisVerticalIcon,
	TrashIcon,
	ChartBarIcon,
	MapPinIcon,
} from "@heroicons/vue/24/outline";
import Modal from "./ui/Modal.vue";
import Dropdown from "./ui/Dropdown.v2.vue";
import DropdownMenu from "./ui/DropdownMenu.vue";
import DropdownMenuItem from "./ui/DropdownMenuItem.vue";
import { mapStores } from "pinia";
import { useLessonsStore } from "@/stores/Lessons.store";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag.ts";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import AgendaGoalEvaluation from "@/components/AgendaGoalEvaluation.vue";
import InsightsDrawer from "@/components/InsightsDrawer.vue";
import { useClassroomsStore } from "@/stores/Classrooms.store.ts";

export default {
	name: "AgendaDayHeader",
	props: {
		sourceDate: {
			type: DateTime, // Luxon DateTime of the day this header is for
			required: true,
		},
	},

	emits: ["requestUnmount"],

	components: {
		// SmsConfirmRemoveCard,
		DocumentDuplicateIcon,
		EllipsisVerticalIcon,
		TrashIcon,
		ChartBarIcon,
		Modal,
		Dropdown,
		DropdownMenu,
		DropdownMenuItem,
		InsightsDrawer,
		MapPinIcon,
		AgendaGoalEvaluation,
	},

	watch: {
		// does this ever get triggered actually??
		sourceDate(newVal, oldVal) {
			this.rebuildWeeksAndSelection();
		},
	},

	data() {
		return {
			showCopyDayDialog: false,
			requiresOverrideConfirmation: false,
			showOutdatedConfirmation: false,
			updateOutdatedLessonPlans: null,
			selectedWeekToCopyTo: null,
			showClearDayConfirmation: false,
			clearingDay: false,
			copyingDay: false,
			selectedDays: [],
			weeksToCopyTo: [],
			weekdaysToCopyTo: [],
			showDayInsights: false,
			showGoalEvaluation: false,
		};
	},

	mounted() {
		this.rebuildWeeksAndSelection();
	},

	computed: {
		...mapStores(useLessonsStore, useOrganisationsStore, useClassroomsStore),

		headerDay(): string {
			return this.sourceDate.toLocaleString({ weekday: "short" });
		},

		headerDate(): string {
			return this.sourceDate.toLocaleString({ day: "numeric" });
		},

		dialogDate(): string {
			return this.sourceDate.toLocaleString({ weekday: "long", day: "numeric", month: "numeric" });
		},

		isTodayOrPast(): boolean {
			return this.sourceDate <= DateTime.local().startOf("day");
		},

		isPast(): boolean {
			return this.sourceDate.endOf("day") < DateTime.local().startOf("day");
		},

		hasLessons(): boolean {
			if (!this.sourceDate) {
				return false;
			}

			return this.lessonsStore.hasBetween(
				this.sourceDate.startOf("day").toJSDate(),
				this.sourceDate.endOf("day").toJSDate()
			);
		},

		dayAsInterval(): Interval {
			return Interval.fromDateTimes(this.sourceDate.startOf("day"), this.sourceDate.endOf("day"));
		},

		canShowGoalEvaluations(): boolean {
			return !!this.organisationsStore.getCurrentOrganisation().curriculum;
		},
	},

	methods: {
		buildWeeks(): any[] {
			const lastMonday = DateTime.local().startOf("week");
			const weeks = [];
			for (let i = 0; i < 52; i++) {
				const week = lastMonday.plus({ weeks: i });
				let label = this.$t("Wk") + ` ${week.weekNumber} : `;
				if (i === 0) {
					label += this.$t("current week");
				} else if (i === 1) {
					label += this.$t("next week");
				} else {
					label +=
						week.toLocaleString({ day: "numeric", month: "short" }) +
						" - " +
						week.plus({ days: 6 }).toLocaleString({ day: "numeric", month: "short" });
				}
				weeks.push({
					label: label,
					key: week.weekNumber,
					value: week,
					fullWeekNumber: week.toFormat("kkkkWW"),
				});
			}
			return weeks;
		},

		selectDefaultDestinationWeek(weeks: any[]) {
			// Special rule: If copying from Friday (any week), default to the week after the source week.
			// Otherwise, use the previous logic.
			const today = DateTime.local();
			const currentWeekStart = today.startOf("week");
			const sourceWeekStart = this.sourceDate.startOf("week");
			const isFriSatSun = today.weekday >= 5; // Fri=5, Sat=6, Sun=7
			let targetWeekStart = currentWeekStart;

			if (this.sourceDate.weekday === 5) {
				// Friday
				// Always pick the week after the source week
				targetWeekStart = sourceWeekStart.plus({ weeks: 1 });
			} else if (sourceWeekStart.toMillis() > currentWeekStart.toMillis()) {
				// Any future week: default to the source week
				targetWeekStart = sourceWeekStart;
			} else if (sourceWeekStart.toMillis() === currentWeekStart.toMillis()) {
				// Same week as today
				targetWeekStart = isFriSatSun ? currentWeekStart.plus({ weeks: 1 }) : currentWeekStart;
			} else {
				// Source in the past: follow the general Fri–Sun vs Mon–Thu rule
				targetWeekStart = isFriSatSun ? currentWeekStart.plus({ weeks: 1 }) : currentWeekStart;
			}

			const targetWeekKey = targetWeekStart.toFormat("kkkkWW");
			const selectedWeek = weeks.find((w: any) => w.fullWeekNumber === targetWeekKey)?.value ?? weeks[0].value;
			this.selectedWeekToCopyTo = selectedWeek;
		},

		// Hide 'current week' only on Fri/Sat/Sun when copying from THIS week; keep it available when copying from next week
		filterWeeksIfNextWeek(weeks: any[]) {
			const today = DateTime.local();
			const currentWeekKey = today.startOf("week").toFormat("kkkkWW");
			const isFriSatSun = today.weekday >= 5; // Mon=1 ... Sun=7
			const sourceInCurrentWeek = this.sourceDate.hasSame(today, "week");
			if (isFriSatSun && sourceInCurrentWeek) {
				return weeks.filter((w: any) => w.fullWeekNumber !== currentWeekKey);
			}
			return weeks;
		},

		rebuildWeeksAndSelection() {
			const weeks = this.buildWeeks();
			this.selectDefaultDestinationWeek(weeks);
			this.weeksToCopyTo = this.filterWeeksIfNextWeek(weeks);
			this.updateWeekdaysToCopyTo();
		},

		// dateFormatting() {
		//     return this.sourceDate.toLocaleString({ weekday: 'short', day: 'numeric', month: 'numeric'})
		// },
		toggleCopyDayDialog(event) {
			// this.$refs.copyDayDialog.toggle(event)
			this.showCopyDayDialog = !this.showCopyDayDialog;
			this.requiresOverrideConfirmation = false;
			this.showOutdatedConfirmation = false;
			this.updateOutdatedLessonPlans = null;
		},

		clearDay() {
			if (!this.hasLessons) {
				return;
			}

			this.lessonsStore.clearErrors();
			this.showClearDayConfirmation = true;
		},

		cloneDay(day: DateTime) {
			if (!this.hasLessons) {
				return;
			}

			this.showCopyDayDialog = true;
			this.requiresOverrideConfirmation = false;
			this.showOutdatedConfirmation = false;
		},

		openGoalEvaluations() {
			if (!this.canShowGoalEvaluations || !this.hasLessons) {
				return;
			}

			this.showGoalEvaluation = true;
		},

		openInsights() {
			if (!this.isTodayOrPast || !this.hasLessons) {
				return;
			}

			this.showDayInsights = true;
		},

		async confirmClearDay() {
			if (this.clearingDay) {
				return;
			}

			this.clearingDay = true;

			try {
				await this.lessonsStore.clearDay(this.sourceDate);
				this.closeClearDayConfirmation();
				this.clearingDay = false;
			} catch (e) {
				if (!(e instanceof ApiErrorMessageBag)) {
					this.clearingDay = false;
					throw e;
				}
			}
		},

		closeClearDayConfirmation() {
			this.showClearDayConfirmation = false;
		},

		updateWeekdaysToCopyTo() {
			this.selectedDays = [];

			this.weekdaysToCopyTo = [];

			let weekdays = 5;
			if (this.organisationsStore?.currentOrganisation.agenda_settings?.include_weekends) {
				weekdays = 7;
			}

			// If copying from Friday, and the destination is the week after, check all days by default
			const copyingFromFriday = this.sourceDate.weekday === 5;
			const destWeekStart = this.selectedWeekToCopyTo.startOf("week");
			const sourceWeekStart = this.sourceDate.startOf("week");
			const isFridayToNextWeek =
				copyingFromFriday && destWeekStart.toMillis() === sourceWeekStart.plus({ weeks: 1 }).toMillis();

			for (let i = 1; i <= weekdays; i++) {
				let startOfWeek = this.selectedWeekToCopyTo.startOf("week");
				if (startOfWeek === null) {
					startOfWeek = this.sourceDate.startOf("week");
				}

				let day = startOfWeek.plus({ days: i - 1 });

				let isCopySource =
					day.weekNumber + "." + day.weekday === this.sourceDate.weekNumber + "." + this.sourceDate.weekday;
				let selectable = !isCopySource && day >= DateTime.local().startOf("day");

				let cantCopyReason = null;
				if (isCopySource) {
					cantCopyReason = this.$t("Cannot copy to same day");
				} else if (!selectable) {
					cantCopyReason = this.$t("Cannot copy to past day");
				}

				this.weekdaysToCopyTo.push({
					name: day.toLocaleString({ weekday: "long" }),
					date: day,
					value: day.weekday,
					selectable: selectable,
					isCopySource: isCopySource,
					cantCopyReason: cantCopyReason,
				});

				if (selectable && day > this.sourceDate) {
					this.selectedDays.push(day.weekday);
				}
			}
		},

		async copyToSelectedDays() {
			this.copyingDay = true;

			let selectedDays = this.selectedDays;
			let sourceRange = Interval.fromDateTimes(
				this.sourceDate.startOf("day"),
				this.sourceDate.startOf("day").plus({ days: 1 })
			);

			let destinations: Interval[] = [];
			for (let i = 0; i < selectedDays.length; i++) {
				let day = this.selectedWeekToCopyTo.plus({ days: selectedDays[i] - 1 });
				let destinationRange = Interval.fromDateTimes(day.startOf("day"), day.startOf("day").plus({ days: 1 }));

				destinations.push(destinationRange);
			}

			try {
				const sourceClassroom = this.classroomsStore.getCurrentClassroom();
				await this.lessonsStore.copyTo(
					sourceClassroom.id,
					sourceRange,
					destinations,
					this.requiresOverrideConfirmation,
					this.updateOutdatedLessonPlans
				);
				this.showCopyDayDialog = false;
				this.requiresOverrideConfirmation = false;
				this.showOutdatedConfirmation = false,
				this.updateOutdatedLessonPlans = null,

				this.$notify({
					text: this.$t('The schedule has been copied successfully.'),
					type: 'success'
				});
			} catch (e) {

				if (e.response?.status === 400) { // requires confirmation
					const errors = e.response.data.errors[""];

					errors.forEach(error => {
						switch (error.key) {
							case 'DESTINATION_NOT_EMPTY':
								this.requiresOverrideConfirmation = true;
								break;

							case 'OUTDATED_LESSON_PLANS':
								this.showOutdatedConfirmation = true;
								this.updateOutdatedLessonPlans = true; // default to true
								break;

							default:
								throw e;
						}
					});
				} else if (e instanceof ApiErrorMessageBag) {
					this.$notify({
						title: "Error!",
						text: e.toLocaleString(this.$i18n),
						type: "error",
					});
				} else {
					throw e;
				}
			} finally {
				this.copyingDay = false;
			}
		},
	},
};
</script>
